import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiFetch } from '@/lib/apiFetch'
import MagnumSays from '@/components/MagnumSays'

interface SessionSummary {
  session: { id: number; childId: number; startedAt: number; endedAt: number | null; exerciseType: string | null }
  totalAttempts: number
  correctAttempts: number
  accuracy: number
  avgResponseMs: number
  durationMs: number
}

interface SessionsResponse {
  sessions: SessionSummary[]
  total: number
}

function formatTimestamp(ts: number): string {
  const d = new Date(ts)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' at ' +
    d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function exerciseLabel(type: string | null): { emoji: string; name: string } {
  switch (type) {
    case 'WORD_DECODE': return { emoji: '🎤', name: 'Word Decode' }
    case 'SYNONYM_MATCH': return { emoji: '🔤', name: 'Synonym Match' }
    case 'SYNONYM_PAIRS': return { emoji: '🧩', name: 'Synonym Pairs' }
    case 'SPELLING': return { emoji: '📝', name: 'Spelling' }
    case 'NOTE_NAMING': return { emoji: '🎵', name: 'Note Naming' }
    case 'INTERVAL_RECOGNITION': return { emoji: '🎼', name: 'Interval Recognition' }
    case 'EAR_TRAINING': return { emoji: '👂', name: 'Ear Training' }
    default: return { emoji: '🎤', name: 'Practice' }
  }
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}m ${s}s`
}

export default function ActivityFeedPage() {
  const { childId } = useParams<{ childId: string }>()
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  const fetchSessions = async (offset: number) => {
    const data = await apiFetch<SessionsResponse>(
      `/dashboard/children/${childId}/sessions?limit=20&offset=${offset}`
    )
    return data
  }

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const data = await fetchSessions(0)
        if (!cancelled) {
          setSessions(data.sessions)
          setTotal(data.total)
        }
      } catch {
        // silently fail
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [childId])

  const loadMore = async () => {
    setLoadingMore(true)
    try {
      const data = await fetchSessions(sessions.length)
      setSessions((prev) => [...prev, ...data.sessions])
      setTotal(data.total)
    } catch {
      // silently fail
    } finally {
      setLoadingMore(false)
    }
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground">Loading activity...</p>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <Link
        to="/dashboard"
        className="text-sm text-muted-foreground hover:text-foreground transition inline-flex items-center gap-1"
      >
        &larr; Back to dashboard
      </Link>

      <h1 className="text-2xl font-bold">Activity</h1>

      {sessions.length === 0 ? (
        <div className="py-4">
          <MagnumSays>No practice sessions yet! Once they start reading, you'll see all their activity here.</MagnumSays>
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => {
            const { emoji, name } = exerciseLabel(s.session.exerciseType)
            return (
              <Link
                key={s.session.id}
                to={`/dashboard/children/${childId}/activity/${s.session.id}`}
                className="block p-4 border border-border rounded-lg hover:border-primary hover:bg-accent/50 transition"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{emoji}</span>
                    <div>
                      <p className="font-medium text-sm">{name}</p>
                      <p className="text-xs text-muted-foreground">{formatTimestamp(s.session.startedAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-right">
                    <div>
                      <p className="font-medium">{Math.round(s.accuracy * 100)}%</p>
                      <p className="text-xs text-muted-foreground">accuracy</p>
                    </div>
                    <div>
                      <p className="font-medium">{formatDuration(s.durationMs)}</p>
                      <p className="text-xs text-muted-foreground">duration</p>
                    </div>
                    <div className="hidden sm:block">
                      <p className="font-medium">{Math.round(s.avgResponseMs / 1000 * 10) / 10}s</p>
                      <p className="text-xs text-muted-foreground">avg response</p>
                    </div>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      )}

      {sessions.length < total && (
        <div className="text-center pt-2">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="px-6 py-3 border border-border rounded-xl font-medium hover:bg-accent transition disabled:opacity-50 min-h-[44px]"
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  )
}
